import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { MaskLines, Eyebrow } from "./Motion";
import { CTAButton } from "./CTAButton";
import { LogoMark } from "./Logo";
import { MilestoneStat } from "./MilestoneStat";
import { LineIcon } from "./LineIcon";
import { IMG, STAKEHOLDERS } from "../../content/site";

const RING = "PEOPLE \u2022 PLANET \u2022 PROGRESS \u2022 PEOPLE \u2022 PLANET \u2022 PROGRESS \u2022";

const TiltBadge = () => {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const sx = useSpring(rx, { stiffness: 120, damping: 14 });
  const sy = useSpring(ry, { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 24);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 24);
  };
  return (
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={() => { rx.set(0); ry.set(0); }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.2, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
      style={{ perspective: 900 }}
      className="hidden lg:block"
      data-testid="hero-tilt-badge"
    >
      <motion.div style={{ rotateX: sx, rotateY: sy, transformStyle: "preserve-3d" }} className="relative grid h-64 w-64 place-items-center rounded-full border border-white/20 bg-white/10 backdrop-blur-xl">
        <svg viewBox="0 0 200 200" className="spin-slow absolute inset-3" aria-hidden="true">
          <defs><path id="ring" d="M100,100 m-84,0 a84,84 0 1,1 168,0 a84,84 0 1,1 -168,0" /></defs>
          <text fill="#F9F8F3" fontSize="10.5" fontFamily="JetBrains Mono, monospace"><textPath href="#ring" textLength="522" lengthAdjust="spacing">{RING}</textPath></text>
        </svg>
        <div style={{ transform: "translateZ(60px)" }} className="rounded-full shadow-2xl">
          <LogoMark className="h-28 w-28" tone="light" />
        </div>
      </motion.div>
    </motion.div>
  );
};

export const HomeHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden bg-[#082619]" data-testid="home-hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.img
          src={IMG.hero}
          alt="Aerial view of industrial factory rooftops covered with solar panels"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1.02 }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
          className="h-full w-full object-cover"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#082619]/95 via-[#082619]/70 to-[#082619]/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#082619] via-transparent to-[#082619]/40" />
      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1440px] flex-col justify-end px-5 pt-32 pb-28 md:px-10 md:pb-28">
        <div className="flex items-end justify-between gap-10">
          <div className="max-w-4xl">
            <Eyebrow tone="light">Renewable Energy Advisory · Mumbai</Eyebrow>
            <h1 className="font-display mt-6 text-[2.6rem] leading-[1] sm:text-6xl lg:text-[4.9rem] font-medium tracking-tight text-white">
              <MaskLines
                delay={0.35}
                lines={[
                  "Powering Businesses",
                  <>Towards a <span className="font-serif-i text-[#74DC26]">Sustainable</span></>,
                  "Future.",
                ]}
              />
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85 }}
              className="mt-7 inline-flex rounded-2xl border border-white/15 bg-white/5 px-5 py-3 backdrop-blur-md"
            >
              <MilestoneStat />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1 }}
              className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-[#E6EDE8]"
            >
              Connecting C&I consumers, renewable energy developers and EPC partners through commercially informed solar solutions.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.15 }}
              className="mt-10 flex flex-wrap gap-3"
            >
              <CTAButton to="/solutions" testId="hero-cta-explore-solutions">Explore Solar Solutions</CTAButton>
              <CTAButton to="/contact" variant="ghostLight" testId="hero-cta-discuss-energy">Discuss Your Energy Requirements</CTAButton>
            </motion.div>
          </div>
          <TiltBadge />
        </div>
      </motion.div>
      <div className="absolute inset-x-0 bottom-0 z-10 border-t border-white/15">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-x-6 gap-y-1 px-5 py-4 md:px-10">
          <span className="eyebrow hidden md:inline !text-[0.6rem] text-[#74DC26]">We connect</span>
          {STAKEHOLDERS.map((s) => (
            <span key={s.id} className="flex items-center gap-2 text-xs md:text-sm text-white/85" data-testid={`hero-stakeholder-${s.id}`}>
              <LineIcon name={s.icon} className="h-4 w-4 text-[#74DC26]" />{s.title}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
